import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Select,
  MenuItem,
  Checkbox,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CalendarToday,
  Business,
  DirectionsCar,
  Numbers,
  Layers,
  Pets,
  Person,
  Badge,
  Save as SaveIcon,
  ContentCopy as ContentCopyIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
/* eslint-disable @typescript-eslint/no-unused-vars */
import { MainSectionPanel, FormSectionPanel, ProductosPorPresentacionTable, type CustomColumn } from '../../../components/common';
import { useGuiaMateriaPrimaStore } from '../guias-materia-prima/store/useGuiaMateriaPrimaStore';
import { useGuiaProduccionStore } from './store/useGuiaProduccionStore';
import PresentacionSelectionModal from '../guias-materia-prima/components/PresentacionSelectionModal';
import type { ArticuloProduccion, GuiaProduccionFormData, EstadoGuiaProduccion, GuiaProduccionDetailResponse } from './types';
import type { ArticuloMateriaPrima } from '../guias-materia-prima/types';
import { guiaRecepcionPdfService, type GuiaRecepcionPdfData } from '../guias-materia-prima/services/guiaRecepcionPdfService';

// Labels para estados
const estadoLabels: Record<EstadoGuiaProduccion, string> = {
  PEN: 'Pendiente',
  APR: 'Aprobado',
  ANU: 'Anulado',
};

interface GuiaMateriaPrimaDetail {
  id: number;
  numeroComprobante: number;
  idDocumento: number;
  documentoNumero: string;
  fechaExtraccion: string;
  proveedorRazonSocial: string;
  asociacion: string;
  numeroMallas: number;
  numeroManojos: number;
  placaVehiculo: string;
  idEspecie: number;
  especieDescripcion: string;
  estado: string;
  idOrigenProducto: number;
  isEntero: boolean;
  detalles: Array<{
    id: number;
    idArticulo: number;
    articuloCodigo: string;
    articuloDescripcion: string;
    cantidad: number;
    coeficiente: number;
    pesoEmpaqueInterior?: number;
    duenioNombre?: string;
    encargadoNombre?: string;
    idPresentacion?: number;
    presentacionDescripcion?: string;
    calibre?: string;
  }>;
}

const GuiaProduccionFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isEdit = id !== 'new';

  // Get parameters from URL
  const idGuiaMateriaPrima = searchParams.get('idGuiaMateriaPrima');
  const idDetalleParam = searchParams.get('idDetalle');
  const readonlyParam = searchParams.get('readonly') === 'true';

  // State to track if guía has a parte (loaded from backend)
  const [tieneParte, setTieneParte] = useState(false);

  // isReadonly is true if URL param is set OR if the guía has a parte
  const isReadonly = readonlyParam || tieneParte;

  // Stores
  const { saveGuia, updateEstado: updateEstadoStore, fetchGuiaById, loading: storeLoading, error: storeError } = useGuiaProduccionStore();
  const { fetchGuiaById: fetchGuiaMateriaPrimaById } = useGuiaMateriaPrimaStore();

  // State for existing guia data (edit mode)
  const [guiaProduccionId, setGuiaProduccionId] = useState<number | undefined>(undefined);
  const [idDetalleMateriaPrima, ] = useState<number | undefined>(
    idDetalleParam ? parseInt(idDetalleParam) : undefined
  );
  // Sub-lote info from backend (for edit mode)
  const [subLoteInfoFromBackend, setSubLoteInfoFromBackend] = useState<{
    subLoteCantidad?: number;
    duenio?: string;
    encargado?: string;
  } | null>(null);

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [guiaMateriaPrima, setGuiaMateriaPrima] = useState<GuiaMateriaPrimaDetail | null>(null);
  const [selectedDetalle, setSelectedDetalle] = useState<GuiaMateriaPrimaDetail['detalles'][0] | null>(null);
  const [productos, setProductos] = useState<ArticuloProduccion[]>([]);
  const [notas, setNotas] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [showPresentacionModal, setShowPresentacionModal] = useState(false);
  const [estado, setEstado] = useState<EstadoGuiaProduccion>('PEN');
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [, setProductosExpanded] = useState(true);

  // Ref for productos terminados section
  const productosTerminadosSectionRef = useRef<HTMLDivElement>(null);

  // Transform backend response detalles to ArticuloProduccion format
  const transformDetallesForEdit = (detalles: GuiaProduccionDetailResponse['detalles']): ArticuloProduccion[] => {
    return detalles.map((detalle, index) => {
      const pesoEmpaqueInterior = detalle.pesoEmpaqueInterior || 0;
      const cantidad = detalle.cantidad;
      const devolucionProveedor = detalle.esDevueltoExterno || false;
      const cantidadDevolucion = detalle.cantidadDevolucion;

      // Calcular diferencia y total basado en devolución
      const cantidadDev = devolucionProveedor ? (cantidadDevolucion ?? cantidad) : 0;
      const diferencia = cantidad - cantidadDev;
      const total = diferencia * pesoEmpaqueInterior;

      return {
        id: detalle.id || index + 1,
        idArticulo: detalle.idArticulo,
        codigoArticulo: detalle.codigoArticulo || '',
        descripcion: detalle.descripcion || '',
        especieNombreComun: '',
        coeficiente: detalle.coeficiente || 0,
        pesoEmpaqueInterior,
        cantidad,
        total,
        calibre: detalle.calibre,
        presentacion: detalle.presentacion,
        idPresentacion: undefined,
        devolucionInterna: detalle.esDevueltoInterno || false,
        devolucionProveedor,
        cantidadDevolucion,
      };
    });
  };

  // Load existing guia de producción for edit mode
  const loadGuiaProduccion = useCallback(async (guiaId: number) => {
    try {
      setLoading(true);
      setError(null);

      const guiaData = await fetchGuiaById(guiaId);

      if (!guiaData) {
        setError('No se encontró la guía de producción');
        setLoading(false);
        return;
      }

      // Store the guia ID for updates
      setGuiaProduccionId(guiaData.id);

      // Check if guía has a parte associated (makes it readonly)
      if (guiaData.tieneParte) {
        setTieneParte(true);
      }

      // Set estado from existing data
      if (guiaData.estado) {
        setEstado(guiaData.estado as EstadoGuiaProduccion);
      }

      // Set observaciones and notas
      setObservaciones(guiaData.observaciones || '');
      setNotas(guiaData.notas || '');

      // Store sub-lote info from backend response (for edit mode display)
      setSubLoteInfoFromBackend({
        subLoteCantidad: guiaData.subLoteCantidad,
        duenio: guiaData.duenio,
        encargado: guiaData.encargado,
      });

      // Transform and set productos
      if (guiaData.detalles && guiaData.detalles.length > 0) {
        const productosTransformed = transformDetallesForEdit(guiaData.detalles);
        setProductos(productosTransformed);
      }

      // Now load the Guia Materia Prima using idGuiaRecepcion from the response
      const guiaMateriaPrimaId = guiaData.idGuiaRecepcion;
      if (guiaMateriaPrimaId) {
        const materiaPrimaData = await fetchGuiaMateriaPrimaById(guiaMateriaPrimaId);
        if (materiaPrimaData) {
          setGuiaMateriaPrima(materiaPrimaData as unknown as GuiaMateriaPrimaDetail);

          // If we have idGuiaRecepcionDetalle, find and set the selectedDetalle
          if (guiaData.idGuiaRecepcionDetalle && materiaPrimaData.detalles) {
            const detalle = materiaPrimaData.detalles.find(
              (d: { id: number }) => d.id === guiaData.idGuiaRecepcionDetalle
            );
            if (detalle) {
              // Ensure required fields have default values
              setSelectedDetalle({
                ...detalle,
                articuloCodigo: detalle.articuloCodigo || '',
                articuloDescripcion: detalle.articuloDescripcion || '',
                cantidad: detalle.cantidad || 0,
                coeficiente: detalle.coeficiente || 0,
              });
            }
          }
        }
      } else {
        setError('La guía de producción no tiene una guía de materia prima asociada');
      }
    } catch (err) {
      console.error('Error loading guia produccion:', err);
      setError('Error al cargar la guía de producción');
    } finally {
      setLoading(false);
    }
  }, [fetchGuiaById, fetchGuiaMateriaPrimaById]);

  // Load Guia Materia Prima data (for create mode)
  const loadGuiaMateriaPrima = useCallback(async () => {
    if (!idGuiaMateriaPrima) {
      setError('No se especificó la guía de materia prima');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await fetchGuiaMateriaPrimaById(parseInt(idGuiaMateriaPrima));
      const guiaData = data as unknown as GuiaMateriaPrimaDetail;
      setGuiaMateriaPrima(guiaData);

      // If idDetalle is provided (Type A), find and set the specific detail
      if (idDetalleMateriaPrima && guiaData.detalles && guiaData.detalles.length > 0) {
        const detalle = guiaData.detalles.find(d => d.id === idDetalleMateriaPrima);
        if (detalle) {
          setSelectedDetalle(detalle);
        } else {
          // If idDetalle not found but detalles exist, use the first one
          setSelectedDetalle(guiaData.detalles[0]);
        }
      } else if (guiaData.detalles && guiaData.detalles.length > 0) {
        // No specific idDetalle provided, use the first detail
        setSelectedDetalle(guiaData.detalles[0]);
      }
    } catch (err) {
      console.error('Error loading guia materia prima:', err);
      setError('Error al cargar la información de la guía de materia prima');
    } finally {
      setLoading(false);
    }
  }, [idGuiaMateriaPrima, idDetalleMateriaPrima, fetchGuiaMateriaPrimaById]);

  useEffect(() => {
    if (isEdit && id) {
      // Edit mode: load existing guia de producción
      loadGuiaProduccion(parseInt(id));
    } else {
      // Create mode: load guia materia prima from URL params
      loadGuiaMateriaPrima();
    }
  }, [isEdit, id, loadGuiaProduccion, loadGuiaMateriaPrima]);

  // Cleanup effect: close modal when component unmounts
  useEffect(() => {
    return () => {
      setShowPresentacionModal(false);
    };
  }, []);

  const handleVolver = () => {
    // Navigate back to guias-produccion list if editing, otherwise to guias-materia-prima
    navigate(isEdit ? '/produccion/guias-produccion' : '/produccion/guias-materia-prima');
  };

  const handlePrint = () => {
    if (!guiaMateriaPrima) {
      setAlertMessage('No hay datos de la guía de materia prima para imprimir.');
      setTimeout(() => setAlertMessage(null), 3000);
      return;
    }

    // Calcular peso de materia prima para el rendimiento
    const pesoMateriaPrima = selectedDetalle
      ? (selectedDetalle.cantidad || 0) * (selectedDetalle.coeficiente || 0)
      : subLoteInfoFromBackend?.subLoteCantidad && guiaMateriaPrima?.detalles?.[0]
        ? (subLoteInfoFromBackend.subLoteCantidad) * (guiaMateriaPrima.detalles[0].coeficiente || 0)
        : guiaMateriaPrima?.detalles?.[0]
          ? (guiaMateriaPrima.detalles[0].cantidad || 0) * (guiaMateriaPrima.detalles[0].coeficiente || 0)
          : 0;

    // Preparar datos para el PDF usando la información de la guía de materia prima
    const pdfData: GuiaRecepcionPdfData = {
      id: guiaMateriaPrima.id,
      idGuiaProduccion: guiaProduccionId, // ID de la guía de producción para N° Orden
      numeroComprobante: guiaMateriaPrima.numeroComprobante,
      idDocumento: guiaMateriaPrima.idDocumento,
      documentoNumero: guiaMateriaPrima.documentoNumero,
      idProveedor: undefined, // No disponible en GuiaMateriaPrimaDetail
      proveedorRazonSocial: guiaMateriaPrima.proveedorRazonSocial,
      isServicio: false,
      observaciones: observaciones,
      notas: notas,
      fechaExtraccion: guiaMateriaPrima.fechaExtraccion,
      asociacion: guiaMateriaPrima.asociacion,
      numeroMallas: guiaMateriaPrima.numeroMallas,
      numeroManojos: guiaMateriaPrima.numeroManojos,
      placaVehiculo: guiaMateriaPrima.placaVehiculo,
      idEspecie: guiaMateriaPrima.idEspecie,
      especieDescripcion: guiaMateriaPrima.especieDescripcion,
      estado: estado,
      idOrigenProducto: guiaMateriaPrima.idOrigenProducto,
      isEntero: guiaMateriaPrima.isEntero,
      pesoMateriaPrima: pesoMateriaPrima,
      detalles: productos
        .map(p => {
          // Calcular la cantidad efectiva considerando devoluciones
          const cantidadDevolucion = p.devolucionProveedor ? (p.cantidadDevolucion ?? p.cantidad) : 0;
          const cantidadEfectiva = p.cantidad - cantidadDevolucion;

          return {
            id: p.id,
            idArticulo: p.idArticulo,
            articuloCodigo: p.codigoArticulo,
            articuloDescripcion: p.descripcion,
            cantidad: cantidadEfectiva, // Usar la diferencia, no la cantidad original
            coeficiente: p.coeficiente,
            descripcion: p.descripcion,
            idDuenio: undefined,
            duenioNombre: subLoteInfoFromBackend?.duenio,
            idEncargado: undefined,
            encargadoNombre: subLoteInfoFromBackend?.encargado,
            idPresentacion: p.idPresentacion,
            presentacionDescripcion: p.presentacion,
            calibre: p.calibre,
            pesoEmpaqueInterior: p.pesoEmpaqueInterior
          };
        })
        .filter(d => d.cantidad > 0) // Excluir productos totalmente devueltos
    };

    guiaRecepcionPdfService.generatePdf(pdfData);
  };

  const handleAddProducto = () => {
    if (guiaMateriaPrima?.idEspecie) {
      setShowPresentacionModal(true);
    }
  };

  const handleSelectProducts = (selectedProducts: ArticuloMateriaPrima[]) => {
    const newProductos: ArticuloProduccion[] = selectedProducts.map(p => ({
      id: p.id,
      idArticulo: p.idArticulo,
      codigoArticulo: p.codigoArticulo,
      descripcion: p.descripcion,
      especieNombreComun: p.especieNombreComun,
      coeficiente: p.coeficiente,
      pesoEmpaqueInterior: p.pesoEmpaqueInterior,
      cantidad: p.cantidad,
      total: p.total,
      calibre: p.calibre,
      presentacion: p.presentacion,
      idPresentacion: p.idPresentacion,
      devolucionInterna: false,
      devolucionProveedor: false,
    }));

    setProductos(prev => [...prev, ...newProductos]);
    setShowPresentacionModal(false);

    // Ensure section is expanded and scroll to it
    setTimeout(() => {
      setProductosExpanded(true);
      setTimeout(() => {
        productosTerminadosSectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 150);
    }, 50);
  };

  // Handle estado change
  const handleEstadoChange = async (event: SelectChangeEvent) => {
    const newEstado = event.target.value as EstadoGuiaProduccion;
    if (isEdit && id) {
      const success = await updateEstadoStore(parseInt(id), newEstado);
      if (success) {
        setEstado(newEstado);
        setAlertMessage(`Estado actualizado a "${estadoLabels[newEstado]}" exitosamente.`);

        // Redirect to list when approved
        if (newEstado === 'APR') {
          setTimeout(() => {
            navigate('/produccion/guias-produccion');
          }, 1500);
        } else {
          setTimeout(() => setAlertMessage(null), 3000);
        }
      } else {
        setError('Error al actualizar el estado.');
      }
    } else {
      setEstado(newEstado);
    }
  };

  // Handle devolucion proveedor change
  const handleDevolucionProveedorChange = (id: number, checked: boolean) => {
    setProductos(prev =>
      prev.map(p => {
        if (p.id !== id) return p;
        const cantidadDevolucion = checked ? p.cantidad : undefined;
        const diferencia = checked ? 0 : p.cantidad;
        return {
          ...p,
          devolucionProveedor: checked,
          cantidadDevolucion,
          total: diferencia * p.pesoEmpaqueInterior
        };
      })
    );
  };

  // Handle cantidad devolucion change
  const handleCantidadDevolucionChange = (id: number, cantidad: number) => {
    setProductos(prev =>
      prev.map(p => {
        if (p.id !== id) return p;
        // No permitir devolver más de lo que hay en cantidad
        const cantidadDevolucionValidada = Math.min(Math.max(0, cantidad), p.cantidad);
        const diferencia = p.cantidad - cantidadDevolucionValidada;
        return {
          ...p,
          cantidadDevolucion: cantidadDevolucionValidada,
          total: diferencia * p.pesoEmpaqueInterior
        };
      })
    );
  };

  // Auto-populate Observaciones based on products marked as Dev. Proveedor
  useEffect(() => {
    const productosDevolucion = productos.filter(p => p.devolucionProveedor);

    if (productosDevolucion.length > 0) {
      const detallesDevolucion = productosDevolucion.map(p => {
        const cantidadDev = p.cantidadDevolucion ?? p.cantidad;
        const pesoTotal = (cantidadDev * p.pesoEmpaqueInterior).toFixed(2);
        return `${pesoTotal}kg de ${p.descripcion}`;
      });

      const textoObservaciones = `El proveedor se lleva: ${detallesDevolucion.join(', ')}`;
      setObservaciones(textoObservaciones);
    } else {
      // Clear observaciones if no products are marked for return
      setObservaciones('');
    }
  }, [productos]);

  // Custom columns for devoluciones
  const devolucionColumns: CustomColumn<ArticuloProduccion>[] = [
    {
      id: 'devolucionProveedor',
      label: 'Dev. Proveedor',
      align: 'center',
      width: 180,
      render: (_value: boolean, row: ArticuloProduccion) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Checkbox
            checked={row.devolucionProveedor || false}
            onChange={(e) => handleDevolucionProveedorChange(row.id, e.target.checked)}
            size="small"
          />
          {row.devolucionProveedor && (
            <TextField
              type="number"
              size="small"
              value={row.cantidadDevolucion ?? row.cantidad}
              onChange={(e) => handleCantidadDevolucionChange(row.id, parseInt(e.target.value) || 0)}
              inputProps={{ min: 0, max: row.cantidad, style: { width: '60px', textAlign: 'center' } }}
              sx={{ width: '80px' }}
            />
          )}
        </Box>
      ),
    },
    {
      id: 'diferencia',
      label: 'Diferencia',
      align: 'center',
      width: 100,
      render: (_value: any, row: ArticuloProduccion) => {
        const cantidadDev = row.devolucionProveedor ? (row.cantidadDevolucion ?? row.cantidad) : 0;
        const diferencia = row.cantidad - cantidadDev;
        return (
          <Chip
            label={diferencia}
            size="small"
            color={diferencia < row.cantidad ? 'warning' : 'default'}
            variant="outlined"
          />
        );
      },
    },
  ];

  const handleCantidadChange = (id: number, cantidad: number) => {
    setProductos(prev =>
      prev.map(p => {
        if (p.id !== id) return p;

        // Si tiene devolución activa, ajustar cantidadDevolucion si excede la nueva cantidad
        let nuevaCantidadDevolucion = p.cantidadDevolucion;
        if (p.devolucionProveedor && p.cantidadDevolucion !== undefined) {
          // Si la cantidadDevolucion era igual a la cantidad anterior, mantenerla sincronizada
          if (p.cantidadDevolucion === p.cantidad) {
            nuevaCantidadDevolucion = cantidad;
          }
          // Si la cantidadDevolucion excede la nueva cantidad, ajustarla
          else if (p.cantidadDevolucion > cantidad) {
            nuevaCantidadDevolucion = cantidad;
          }
        }

        const cantidadDev = p.devolucionProveedor ? (nuevaCantidadDevolucion ?? cantidad) : 0;
        const diferencia = cantidad - cantidadDev;
        return {
          ...p,
          cantidad,
          cantidadDevolucion: nuevaCantidadDevolucion,
          total: diferencia * p.pesoEmpaqueInterior
        };
      })
    );
  };

  const handleRemoveProducto = (id: number) => {
    setProductos(prev => prev.filter(p => p.id !== id));
  };

  // Handle "Misma Salida" - copy products from materia prima to productos terminados
  // Only available for Product Type B (idOrigenProducto === 2) and NOT "entera"
  const handleMismaSalida = () => {
    if (!guiaMateriaPrima?.detalles || guiaMateriaPrima.detalles.length === 0) {
      setError('No hay productos en la guía de materia prima para copiar');
      return;
    }

    // Transform materia prima detalles to ArticuloProduccion format
    const copiedProducts: ArticuloProduccion[] = guiaMateriaPrima.detalles.map((detalle, index) => {
      const pesoEmpaque = detalle.pesoEmpaqueInterior || 0;
      const cantidad = detalle.cantidad || 0;

      return {
        id: Date.now() + index, // Generate unique ID
        idArticulo: detalle.idArticulo,
        codigoArticulo: detalle.articuloCodigo || '',
        descripcion: detalle.articuloDescripcion || '',
        especieNombreComun: guiaMateriaPrima.especieDescripcion || '',
        coeficiente: detalle.coeficiente || 0,
        pesoEmpaqueInterior: pesoEmpaque, // Use pesoEmpaqueInterior from backend
        cantidad: cantidad,
        total: pesoEmpaque * cantidad, // Calculate total using pesoEmpaqueInterior × cantidad
        calibre: detalle.calibre || undefined,
        presentacion: detalle.presentacionDescripcion || undefined,
        idPresentacion: detalle.idPresentacion || undefined,
        devolucionInterna: false,
        devolucionProveedor: false,
      };
    });

    setProductos(copiedProducts);
    setAlertMessage('Productos copiados desde la guía de materia prima');
    setTimeout(() => setAlertMessage(null), 3000);
  };

  // Determine if "Misma Salida" button should be shown
  // Only for Product Type B (idOrigenProducto === 2) and NOT marked as "entera"
  const showMismaSalidaButton = guiaMateriaPrima &&
    guiaMateriaPrima.idOrigenProducto === 2 &&
    !guiaMateriaPrima.isEntero &&
    !isReadonly &&
    !isEdit; // Only show for new guías, not edit mode

  // Determine if materia prima detail section should be shown
  // Show for Product Type B (idOrigenProducto === 2) regardless of "isEntero"
  const showMateriaPrimaDetail = guiaMateriaPrima &&
    guiaMateriaPrima.idOrigenProducto === 2;

  const handleSubmit = async () => {
    if (!guiaMateriaPrima || productos.length === 0) {
      setError('Debe agregar al menos un producto');
      return;
    }

    const productosConCantidad = productos.filter(p => p.cantidad > 0);
    if (productosConCantidad.length === 0) {
      setError('Al menos un producto debe tener cantidad mayor a 0');
      return;
    }

    setError(null);

    const formData: GuiaProduccionFormData = {
      id: guiaProduccionId,
      idGuiaMateriaPrima: guiaMateriaPrima.id,
      idDetalleMateriaPrima: selectedDetalle?.id || idDetalleMateriaPrima,
      notas,
      observaciones,
      detalles: productosConCantidad.map(p => ({
        idArticulo: p.idArticulo,
        cantidad: p.cantidad,
        coeficiente: p.coeficiente,
        descripcion: p.descripcion,
        devolucionInterna: p.devolucionInterna,
        devolucionProveedor: p.devolucionProveedor,
      })),
    };

    const savedId = await saveGuia(formData, productosConCantidad);

    if (savedId) {
      setAlertMessage('Guía de producción guardada exitosamente');
      setTimeout(() => {
        navigate('/produccion/guias-produccion');
      }, 1000);
    } else {
      setError(storeError || 'Error al guardar la guía de producción');
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('es-ES');
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <MainSectionPanel>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </MainSectionPanel>
    );
  }

  return (
    <MainSectionPanel>
      <Paper sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleVolver}
              sx={{ mr: 2 }}
            >
              Volver
            </Button>
            <Typography variant="h5" component="h1" fontWeight={600}>
              {isReadonly ? 'Ver Guía de Producción' : (isEdit ? 'Editar Guía de Producción' : 'Nueva Guía de Producción')}
            </Typography>
          </Box>
          {isEdit && estado === 'APR' && (
            <Button
              variant="outlined"
              color="info"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
            >
              Imprimir PDF
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {alertMessage && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setAlertMessage(null)}>
            {alertMessage}
          </Alert>
        )}

        {/* Tipo de Producto y Estado */}
        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {guiaMateriaPrima && (
            <Chip
              label={`Tipo ${guiaMateriaPrima.idOrigenProducto === 1 ? 'A' : 'B'}`}
              color={guiaMateriaPrima.idOrigenProducto === 1 ? 'primary' : 'error'}
              size="medium"
              variant="filled"
              sx={{ fontWeight: 600 }}
            />
          )}

          {/* Estado selector */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Estado:</Typography>
            {tieneParte ? (
              <Chip
                label="Con Parte"
                color="info"
                size="small"
              />
            ) : isReadonly ? (
              <Chip
                label={estadoLabels[estado]}
                color={estado === 'PEN' ? 'warning' : estado === 'APR' ? 'success' : 'error'}
                size="small"
              />
            ) : (
              <Select
                value={estado}
                onChange={handleEstadoChange}
                size="small"
                disabled={storeLoading}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="PEN">
                  <Chip label="Pendiente" color="warning" size="small" />
                </MenuItem>
                <MenuItem value="APR">
                  <Chip label="Aprobado" color="success" size="small" />
                </MenuItem>
                <MenuItem value="ANU">
                  <Chip label="Anulado" color="error" size="small" />
                </MenuItem>
              </Select>
            )}
          </Box>
        </Box>

        {guiaMateriaPrima && (
          <>
            {/* DERC Info Section */}
            <FormSectionPanel title="Información del DERC">
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Numbers fontSize="small" color="primary" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        N° DERC
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {guiaMateriaPrima.documentoNumero || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarToday fontSize="small" color="primary" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Fecha de Extracción
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {formatDate(guiaMateriaPrima.fechaExtraccion)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Business fontSize="small" color="primary" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Asociación
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {guiaMateriaPrima.asociacion || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Numbers fontSize="small" color="primary" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        N° Mallas
                      </Typography>
                      <Chip
                        label={guiaMateriaPrima.numeroMallas}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Person fontSize="small" color="primary" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Proveedor
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {guiaMateriaPrima.proveedorRazonSocial || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Layers fontSize="small" color="primary" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        N° Manojos
                      </Typography>
                      <Chip
                        label={guiaMateriaPrima.numeroManojos || 0}
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DirectionsCar fontSize="small" color="primary" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Placa de Vehículo
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {guiaMateriaPrima.placaVehiculo || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Pets fontSize="small" color="primary" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Especie
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {guiaMateriaPrima.especieDescripcion || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Show "Es Entero" only for Product Type B */}
                {guiaMateriaPrima.idOrigenProducto === 2 && (
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Layers fontSize="small" color="primary" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Es Entero
                        </Typography>
                        <Chip
                          label={guiaMateriaPrima.isEntero ? 'Sí' : 'No'}
                          size="small"
                          color={guiaMateriaPrima.isEntero ? 'success' : 'default'}
                          variant="filled"
                        />
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </FormSectionPanel>

            {/* Guia Materia Prima Info Section */}
            <FormSectionPanel title="Guía de Materia Prima">
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Numbers fontSize="small" color="info" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        N° Guía MP
                      </Typography>
                      <Chip
                        label={guiaMateriaPrima.numeroComprobante || guiaMateriaPrima.id}
                        size="small"
                        color="info"
                      />
                    </Box>
                  </Box>
                </Grid>

                {/* N° Sub Lote - Only show for Product Type A */}
                {guiaMateriaPrima.idOrigenProducto === 1 && (
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Badge fontSize="small" color="info" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          N° Sub Lote
                        </Typography>
                        <Chip
                          label={selectedDetalle?.cantidad || subLoteInfoFromBackend?.subLoteCantidad || guiaMateriaPrima.detalles?.[0]?.cantidad || 0}
                          size="small"
                          color="warning"
                        />
                      </Box>
                    </Box>
                  </Grid>
                )}

                {/* Dueño - Only show for Product Type A */}
                {guiaMateriaPrima.idOrigenProducto === 1 && (
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Person fontSize="small" color="info" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Dueño
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {selectedDetalle?.duenioNombre || subLoteInfoFromBackend?.duenio || guiaMateriaPrima.detalles?.[0]?.duenioNombre || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}

                {/* Encargado - Only show for Product Type A */}
                {guiaMateriaPrima.idOrigenProducto === 1 && (
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Person fontSize="small" color="info" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Encargado
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {selectedDetalle?.encargadoNombre || subLoteInfoFromBackend?.encargado || guiaMateriaPrima.detalles?.[0]?.encargadoNombre || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </FormSectionPanel>

            {/* Detalle de Materia Prima Section - Only for Product Type B and NOT "entera" */}
            {showMateriaPrimaDetail && guiaMateriaPrima.detalles && guiaMateriaPrima.detalles.length > 0 && (
              <FormSectionPanel title="Detalle de Materia Prima">
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f5f5f5' }}>
                        <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', fontWeight: 600 }}>Código</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', fontWeight: 600 }}>Descripción</th>
                        <th style={{ padding: '12px 16px', textAlign: 'center', borderBottom: '2px solid #e0e0e0', fontWeight: 600 }}>Cantidad</th>
                        <th style={{ padding: '12px 16px', textAlign: 'center', borderBottom: '2px solid #e0e0e0', fontWeight: 600 }}>Peso Empaque</th>
                        <th style={{ padding: '12px 16px', textAlign: 'center', borderBottom: '2px solid #e0e0e0', fontWeight: 600 }}>Total</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', fontWeight: 600 }}>Dueño</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', fontWeight: 600 }}>Encargado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {guiaMateriaPrima.detalles.map((detalle, index) => {
                        const pesoEmpaque = detalle.pesoEmpaqueInterior || 0;
                        const cantidad = detalle.cantidad || 0;
                        const total = pesoEmpaque * cantidad;

                        return (
                          <tr key={detalle.id || index} style={{ borderBottom: '1px solid #e0e0e0' }}>
                            <td style={{ padding: '10px 16px' }}>
                              <Chip label={detalle.articuloCodigo || '-'} size="small" variant="outlined" />
                            </td>
                            <td style={{ padding: '10px 16px' }}>{detalle.articuloDescripcion || '-'}</td>
                            <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                              <Chip label={cantidad} size="small" color="primary" />
                            </td>
                            <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                              {pesoEmpaque.toFixed(2)}
                            </td>
                            <td style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 600 }}>
                              {total.toFixed(2)}
                            </td>
                            <td style={{ padding: '10px 16px' }}>{detalle.duenioNombre || '-'}</td>
                            <td style={{ padding: '10px 16px' }}>{detalle.encargadoNombre || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Box>
              </FormSectionPanel>
            )}

            {/* Productos Terminados Section */}
            <div ref={productosTerminadosSectionRef}>
              <FormSectionPanel
                title="Productos Terminados"
                collapsible={false}
                actions={
                  showMismaSalidaButton ? (
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      startIcon={<ContentCopyIcon />}
                      onClick={handleMismaSalida}
                    >
                      Misma Salida
                    </Button>
                  ) : undefined
                }
              >
                <ProductosPorPresentacionTable
                  productos={productos}
                  onCantidadChange={isReadonly ? undefined : handleCantidadChange}
                  onRemoveProducto={isReadonly ? undefined : handleRemoveProducto}
                  onAddProducto={isReadonly ? undefined : handleAddProducto}
                  addButtonText="Agregar Producto"
                  emptyMessage="No hay productos agregados"
                  emptySubMessage={isReadonly ? "No hay productos en esta guía" : "Haga clic en 'Agregar Producto' para seleccionar productos por presentación"}
                  showPesoEmpaque={true}
                  showCalibre={true}
                  showPresentacionColumn={false}
                  customColumns={isReadonly ? undefined : devolucionColumns}
                  customColumnsPosition="after-cantidad"
                  showRendimiento={guiaMateriaPrima?.idOrigenProducto === 1}
                  pesoMateriaPrima={
                    selectedDetalle
                      ? (selectedDetalle.cantidad || 0) * (selectedDetalle.coeficiente || 0)
                      : subLoteInfoFromBackend?.subLoteCantidad && guiaMateriaPrima?.detalles?.[0]
                        ? (subLoteInfoFromBackend.subLoteCantidad) * (guiaMateriaPrima.detalles[0].coeficiente || 0)
                        : guiaMateriaPrima?.detalles?.[0]
                          ? (guiaMateriaPrima.detalles[0].cantidad || 0) * (guiaMateriaPrima.detalles[0].coeficiente || 0)
                          : 0
                  }
                />
              </FormSectionPanel>
            </div>

            {/* Additional Info Section */}
            <FormSectionPanel title="Información Adicional">
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Notas"
                    value={notas}
                    onChange={(e) => setNotas(e.target.value)}
                    placeholder="Notas adicionales"
                    multiline
                    rows={4}
                    disabled={isReadonly}
                    slotProps={{
                      input: {
                        readOnly: isReadonly,
                      },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Observaciones"
                    value={observaciones}
                    onChange={(e) => setObservaciones(e.target.value)}
                    placeholder="Ej: El proveedor se lleva: Producto X - 10kg, Producto Y - 5kg"
                    multiline
                    rows={4}
                    disabled={isReadonly}
                    slotProps={{
                      input: {
                        readOnly: isReadonly,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </FormSectionPanel>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
                onClick={handleVolver}
                disabled={storeLoading}
              >
                {isReadonly ? 'Volver' : 'Cancelar'}
              </Button>
              {!isReadonly && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={storeLoading ? <CircularProgress size={20} /> : <SaveIcon />}
                  onClick={handleSubmit}
                  disabled={storeLoading || productos.length === 0}
                >
                  {storeLoading ? 'Guardando...' : 'Guardar'}
                </Button>
              )}
            </Box>
          </>
        )}
      </Paper>

      {/* Presentacion Selection Modal */}
      {guiaMateriaPrima && (
        <PresentacionSelectionModal
          open={showPresentacionModal}
          onClose={() => setShowPresentacionModal(false)}
          onSelectProducts={handleSelectProducts}
          idEspecie={guiaMateriaPrima.idEspecie}
          especieNombre={guiaMateriaPrima.especieDescripcion}
        />
      )}
    </MainSectionPanel>
  );
};

export default GuiaProduccionFormPage;
